import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FiActivity,
  FiArrowRight,
  FiBarChart2,
  FiBookOpen,
  FiBriefcase,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiLink,
  FiRefreshCw,
  FiTrendingDown,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle
} from 'react-icons/fi';

import SafeIcon from '../common/SafeIcon';
import { useClient } from '../context/ClientContext';

import { dashboardService } from '../services/dashboardService';
import { coaService } from '../services/coaService';
import { transactionService } from '../services/transactionService';
import { invoiceService } from '../services/invoiceService';
import { bankService } from '../services/bankService';
import { reconciliationService } from '../services/reconciliationService';

/*
 * =========================================================
 * CACHE
 *
 * In-memory only.
 * Scoped by authenticated user and client.
 * Short expiry prevents unnecessary reloads when
 * navigating away from the Dashboard and back.
 * =========================================================
 */

const CACHE_DURATION = 60 * 1000;
const dashboardCache = new Map();

const getCachedDashboard = (key) => {
  const cached = dashboardCache.get(key);

  if (!cached) return null;

  if (Date.now() - cached.savedAt > CACHE_DURATION) {
    dashboardCache.delete(key);
    return null;
  }

  return cached.data;
};

const saveCachedDashboard = (key, data) => {
  dashboardCache.set(key, {
    savedAt: Date.now(),
    data
  });

  // Bound memory usage.
  while (dashboardCache.size > 12) {
    const oldestKey = dashboardCache.keys().next().value;
    dashboardCache.delete(oldestKey);
  }
};

/*
 * =========================================================
 * HELPERS
 * =========================================================
 */

const asArray = (value) =>
  Array.isArray(value) ? value : [];

const toNumber = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const localToday = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

const formatDate = (value) => {
  if (!value) return 'Date unavailable';

  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T12:00:00`
      : value
  );

  if (Number.isNaN(date.getTime())) {
    return 'Date unavailable';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatAmount = (value, currency) => {
  const amount = toNumber(value);

  try {
    if (currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      }).format(amount);
    }

    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
};

const ACCOUNT_TYPES = {
  revenue: ['income', 'revenue'],
  expense: ['expense', 'expenses']
};

const CASH_WORDS = [
  'bank',
  'cash',
  'checking',
  'savings',
  'money market',
  'petty cash',
  'operating account'
];

const getAccountType = (account) =>
  String(account?.type || '')
    .trim()
    .toLowerCase();

const isCashAccount = (account) => {
  if (getAccountType(account) !== 'asset') {
    return false;
  }

  const searchable = [
    account.name,
    account.sub_type
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return CASH_WORDS.some((word) =>
    searchable.includes(word)
  );
};

const isBankOrCardAccount = (account) => {
  if (isCashAccount(account)) return true;

  const searchable = [
    account.name,
    account.sub_type
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    getAccountType(account) === 'liability' &&
    searchable.includes('credit card')
  );
};

/*
 * =========================================================
 * FINANCIAL OVERVIEW
 *
 * Operational overview based on the ledger transactions
 * returned by the existing service.
 *
 * It is not a replacement for authoritative
 * profit/loss, balance sheet or cash flow reports.
 * =========================================================
 */

const buildFinancialOverview = ({
  accounts,
  transactions,
  invoices,
  banks,
  reconciliations
}) => {
  const today = localToday();
  const year = new Date().getFullYear();

  const accountById = new Map(
    accounts.map((account) => [
      account.id,
      account
    ])
  );

  let revenue = 0;
  let expenses = 0;
  let cashMovement = 0;

  const monthlyValues = new Map();

  for (let month = 0; month < 12; month += 1) {
    monthlyValues.set(month, {
      revenue: 0,
      expenses: 0
    });
  }

  for (const transaction of transactions) {
    const account = accountById.get(
      transaction.coa_id
    );

    if (!account) continue;

    const accountType = getAccountType(account);
    const debit = toNumber(transaction.debit);
    const credit = toNumber(transaction.credit);

    const transactionDate = String(
      transaction.date || ''
    ).slice(0, 10);

    // Ledger entries are counted only through today
    // for the current-year overview.
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(transactionDate) ||
      transactionDate > today
    ) {
      continue;
    }

    if (isCashAccount(account)) {
      cashMovement += debit - credit;
    }

    if (
      Number(transactionDate.slice(0, 4)) !== year
    ) {
      continue;
    }

    const month =
      Number(transactionDate.slice(5, 7)) - 1;

    if (!monthlyValues.has(month)) continue;

    const monthly = monthlyValues.get(month);

    if (ACCOUNT_TYPES.revenue.includes(accountType)) {
      const amount = credit - debit;

      revenue += amount;
      monthly.revenue += amount;
    }

    if (ACCOUNT_TYPES.expense.includes(accountType)) {
      const amount = debit - credit;

      expenses += amount;
      monthly.expenses += amount;
    }
  }

  const currentMonth = new Date().getMonth();

  const trends = Array.from(
    { length: currentMonth + 1 },
    (_, month) => {
      const value = monthlyValues.get(month);

      return {
        month: new Date(year, month, 1)
          .toLocaleDateString(undefined, {
            month: 'short'
          }),
        revenue: value.revenue,
        expenses: value.expenses
      };
    }
  );

  const todayDate = today;

  const overdueInvoices = invoices.filter((invoice) => {
    const status = String(
      invoice.status || ''
    ).toLowerCase();

    const excluded = [
      'paid',
      'draft',
      'void',
      'cancelled',
      'canceled'
    ];

    return (
      !excluded.includes(status) &&
      Boolean(invoice.due_date) &&
      String(invoice.due_date).slice(0, 10) < todayDate
    );
  });

  const reconciliationAccountIds = new Set(
    accounts
      .filter(isBankOrCardAccount)
      .map((account) => account.id)
  );

  const unreconciledEntries = transactions.filter(
    (transaction) => (
      reconciliationAccountIds.has(
        transaction.coa_id
      ) &&
      String(transaction.status || '')
        .toLowerCase() !== 'reconciled'
    )
  );

  const lastReconciliation = [...reconciliations]
    .filter((item) =>
      String(item.status).toUpperCase() === 'RECONCILED'
    )
    .sort((a, b) =>
      String(b.statement_date || '')
        .localeCompare(String(a.statement_date || ''))
    )[0];

  // Group journal lines to avoid repeating entries
  // from the same accounting batch.
  const seen = new Set();

  const recentActivity = [...transactions]
    .sort((a, b) =>
      `${b.date || ''}|${b.created_at || ''}`
        .localeCompare(
          `${a.date || ''}|${a.created_at || ''}`
        )
    )
    .filter((transaction) => {
      const key =
        transaction.batch_id || transaction.id;

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    })
    .slice(0, 6);

  return {
    revenue,
    expenses,
    netIncome: revenue - expenses,
    cashMovement,
    trends,
    overdueInvoices: overdueInvoices.length,
    invoiceCount: invoices.length,
    bankCount: banks.length,
    unreconciledCount: unreconciledEntries.length,
    lastReconciliationDate:
      lastReconciliation?.statement_date || null,
    recentActivity,
    transactionCount: transactions.length
  };
};

/*
 * =========================================================
 * SHARED UI
 * =========================================================
 */

function Panel({
  title,
  description,
  action,
  children,
  className = ''
}) {
  return (
    <section
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-900/[0.02] ${className}`}
    >
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">
            {title}
          </h2>

          {description && (
            <p className="mt-1 text-xs text-slate-500">
              {description}
            </p>
          )}
        </div>

        {action}
      </div>

      <div className="p-5">
        {children}
      </div>
    </section>
  );
}

function MetricCard({
  title,
  value,
  icon: Icon,
  description,
  negative = false
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/[0.02]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-slate-500">
          {title}
        </span>

        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <SafeIcon icon={Icon} className="text-base" />
        </span>
      </div>

      <p className={`break-words text-2xl font-semibold tracking-tight ${
        negative ? 'text-rose-600' : 'text-slate-900'
      }`}>
        {value}
      </p>

      <p className="mt-2 text-[11px] text-slate-400">
        {description}
      </p>
    </div>
  );
}

function TextLink({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:text-emerald-800"
    >
      {children}
      <SafeIcon icon={FiArrowRight} />
    </button>
  );
}

function IncomeExpenseChart({
  data,
  currency
}) {
  const maxValue = Math.max(
    1,
    ...data.flatMap((month) => [
      Math.abs(month.revenue),
      Math.abs(month.expenses)
    ])
  );

  const hasActivity = data.some(
    (month) =>
      month.revenue !== 0 ||
      month.expenses !== 0
  );

  if (!hasActivity) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
        <SafeIcon
          icon={FiBarChart2}
          className="mb-3 text-2xl text-slate-300"
        />

        <p className="text-sm font-semibold text-slate-700">
          No ledger activity yet
        </p>

        <p className="mt-1 max-w-xs text-xs leading-relaxed text-slate-500">
          Monthly income and expenses will appear here
          as accounting entries are recorded.
        </p>
      </div>
    );
  }

  const getWidth = (value) =>
    value === 0
      ? '0%'
      : `${Math.max(
          1,
          (Math.abs(value) / maxValue) * 100
        )}%`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />
          Revenue
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />
          Expenses
        </div>
      </div>

      <div className="max-h-[340px] space-y-3 overflow-y-auto pr-1">
        {data.map((month, index) => (
          <div
            key={`${month.month}-${index}`}
            className="grid grid-cols-[35px_minmax(0,1fr)] items-center gap-3"
          >
            <span className="text-xs font-medium text-slate-500">
              {month.month}
            </span>

            <div className="space-y-1.5">
              <div
                className="flex h-3 items-center rounded-sm bg-slate-50"
                title={`Revenue: ${formatAmount(
                  month.revenue,
                  currency
                )}`}
              >
                <div
                  className="h-3 rounded-sm bg-emerald-500"
                  style={{
                    width: getWidth(month.revenue)
                  }}
                />
              </div>

              <div
                className="flex h-3 items-center rounded-sm bg-slate-50"
                title={`Expenses: ${formatAmount(
                  month.expenses,
                  currency
                )}`}
              >
                <div
                  className="h-3 rounded-sm bg-slate-400"
                  style={{
                    width: getWidth(month.expenses)
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] leading-relaxed text-slate-400">
        Bar length shows absolute activity; hover to see
        signed values. Figures are derived from loaded ledger entries.
      </p>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  description,
  onClick
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-emerald-200 hover:bg-emerald-50/40"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700">
        <SafeIcon icon={Icon} className="text-lg" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-slate-800">
          {label}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-500">
          {description}
        </p>
      </div>

      <SafeIcon
        icon={FiArrowRight}
        className="shrink-0 text-slate-400 group-hover:text-emerald-700"
      />
    </button>
  );
}

/*
 * =========================================================
 * DASHBOARD
 * =========================================================
 */

export default function Dashboard() {
  const navigate = useNavigate();

  const {
    user,
    activeFirm,
    activeClient,
    isLoading: workspaceLoading,
    workspaceError
  } = useClient();

  const [dashboardState, setDashboardState] = useState({
    key: null,
    data: null,
    error: null,
    loading: false
  });

  const [refreshVersion, setRefreshVersion] = useState(0);

  const scopeKey =
    user?.id && activeClient?.id
      ? `${user.id}:${activeClient.id}`
      : null;

  /*
   * -------------------------------------------------------
   * FETCH REAL ACCOUNTING DATA
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (workspaceLoading || !scopeKey || !activeClient?.id) {
      return undefined;
    }

    let cancelled = false;

    const cached = getCachedDashboard(scopeKey);

    if (cached && refreshVersion === 0) {
      setDashboardState({
        key: scopeKey,
        data: cached,
        error: null,
        loading: false
      });

      return undefined;
    }

    setDashboardState((previous) => ({
      key: scopeKey,
      data:
        previous.key === scopeKey
          ? previous.data
          : null,
      error: null,
      loading: true
    }));

    const loadDashboard = async () => {
      try {
        /*
         * The existing service resolves the authorized
         * accounting scope for the selected client.
         */

        const scope =
          await dashboardService.getClientDashboardScope(
            activeClient.id
          );

        if (cancelled) return;

        const accountId =
          scope?.financial_account_id ||
          scope?.organization_id;

        if (!accountId) {
          throw new Error(
            'No financial organization is available for this client.'
          );
        }

        const [
          coaResult,
          transactionsResult,
          invoicesResult,
          banksResult,
          reconciliationsResult
        ] = await Promise.all([
          coaService.getChartOfAccounts(accountId),
          transactionService.getTransactions(
            {},
            accountId
          ),
          invoiceService.getInvoices(accountId),
          bankService.getConnectedBanks(accountId),
          reconciliationService.getReconciliations(
            {},
            accountId
          )
        ]);

        if (cancelled) return;

        const accounts = asArray(coaResult);
        const transactions = asArray(
          transactionsResult
        );
        const invoices = asArray(invoicesResult);
        const banks = asArray(banksResult);
        const reconciliations = asArray(
          reconciliationsResult
        );

        const overview = buildFinancialOverview({
          accounts,
          transactions,
          invoices,
          banks,
          reconciliations
        });

        const result = {
          ...overview,
          accountId
        };

        saveCachedDashboard(scopeKey, result);

        if (!cancelled) {
          setDashboardState({
            key: scopeKey,
            data: result,
            error: null,
            loading: false
          });
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Dashboard data load failed:',
          error
        );

        setDashboardState({
          key: scopeKey,
          data: null,
          error:
            error?.message ||
            'Unable to load accounting information.',
          loading: false
        });
      }
    };

    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [
    activeClient?.id,
    scopeKey,
    workspaceLoading,
    refreshVersion
  ]);

  /*
   * -------------------------------------------------------
   * REFRESH
   * -------------------------------------------------------
   */

  const refreshDashboard = () => {
    if (scopeKey) {
      dashboardCache.delete(scopeKey);
    }

    setRefreshVersion((value) => value + 1);
  };

  /*
   * -------------------------------------------------------
   * DISPLAY STATE
   * -------------------------------------------------------
   */

  const hasClient = Boolean(activeClient?.id);

  const hasCurrentData =
    dashboardState.key === scopeKey &&
    Boolean(dashboardState.data);

  const dashboardData = hasCurrentData
    ? dashboardState.data
    : null;

  const dashboardError =
    dashboardState.key === scopeKey
      ? dashboardState.error
      : null;

  const isDashboardLoading =
    workspaceLoading ||
    (
      hasClient &&
      (
        !hasCurrentData &&
        !dashboardError
      )
    );

  const currency =
    activeClient?.currency ||
    null;

  const year = new Date().getFullYear();

  const metricCards = useMemo(() => {
    if (!dashboardData) return [];

    return [
      {
        title: 'Revenue',
        value: formatAmount(
          dashboardData.revenue,
          currency
        ),
        description: `${year} · Recorded income`,
        icon: FiTrendingUp
      },
      {
        title: 'Expenses',
        value: formatAmount(
          dashboardData.expenses,
          currency
        ),
        description: `${year} · Recorded expenses`,
        icon: FiTrendingDown
      },
      {
        title: 'Net Income',
        value: formatAmount(
          dashboardData.netIncome,
          currency
        ),
        description: `${year} · Revenue less expenses`,
        icon: FiBarChart2,
        negative: dashboardData.netIncome < 0
      },
      {
        title: 'Cash Movement',
        value: formatAmount(
          dashboardData.cashMovement,
          currency
        ),
        description: 'Net movement in identified cash accounts',
        icon: FiDollarSign,
        negative: dashboardData.cashMovement < 0
      }
    ];
  }, [dashboardData, currency, year]);

  /*
   * -------------------------------------------------------
   * INITIAL LOADING
   * -------------------------------------------------------
   */

  if (isDashboardLoading) {
    return (
      <div className="flex min-h-[55vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <SafeIcon
            icon={FiRefreshCw}
            className="animate-spin text-2xl text-emerald-600"
          />

          <p className="text-sm font-medium text-slate-500">
            Loading accounting overview...
          </p>
        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * FIRM HAS NO ACTIVE CLIENT
   * -------------------------------------------------------
   */

  if (!hasClient && activeFirm) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
            Firm Workspace
          </p>

          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
            {activeFirm.name}
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            Manage your clients and their accounting
            from one workspace.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <SafeIcon
              icon={FiBriefcase}
              className="text-xl"
            />
          </div>

          <h2 className="mt-5 text-xl font-semibold text-slate-900">
            No client selected
          </h2>

          <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-500">
            Your firm workspace is ready. Select or add
            a client to view financial activity, manage
            invoices, connect bank accounts, and review
            accounting reports.
          </p>

          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Manage Clients
            <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * WORKSPACE UNAVAILABLE
   * -------------------------------------------------------
   */

  if (!hasClient) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <SafeIcon
          icon={FiAlertCircle}
          className="mx-auto text-2xl text-slate-400"
        />

        <h2 className="mt-4 text-lg font-semibold text-slate-900">
          Workspace unavailable
        </h2>

        <p className="mt-2 text-sm text-slate-500">
          {workspaceError ||
            'No authorized accounting workspace is available.'}
        </p>
      </div>
    );
  }

  /*
   * -------------------------------------------------------
   * DASHBOARD LOAD ERROR
   * -------------------------------------------------------
   */

  if (dashboardError && !dashboardData) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-rose-100 bg-white p-8 text-center shadow-sm">
        <SafeIcon
          icon={FiAlertCircle}
          className="mx-auto text-2xl text-rose-500"
        />

        <h2 className="mt-4 text-lg font-semibold text-slate-900">
          Unable to load financial overview
        </h2>

        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          {dashboardError}
        </p>

        <button
          type="button"
          onClick={refreshDashboard}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <SafeIcon icon={FiRefreshCw} />
          Try Again
        </button>
      </div>
    );
  }

  if (!dashboardData) return null;

  /*
   * =======================================================
   * MAIN DASHBOARD
   * =======================================================
   */

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 pb-8">

      {/* PAGE HEADER */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-slate-900">
              Financial Overview
            </h1>

            <span className="rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
              {year}
            </span>
          </div>

          <p className="mt-2 truncate text-sm text-slate-500">
            {activeClient.name}
            {currency ? ` · ${currency}` : ' · Currency not set'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshDashboard}
            disabled={dashboardState.loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <SafeIcon
              icon={FiRefreshCw}
              className={
                dashboardState.loading
                  ? 'animate-spin'
                  : ''
              }
            />
            Refresh
          </button>

          <button
            type="button"
            onClick={() =>
              navigate('/reports/profit-loss')
            }
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            View Reports
            <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
      </div>

      {/* FINANCIAL SUMMARY */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metricCards.map((card) => (
          <MetricCard
            key={card.title}
            {...card}
          />
        ))}
      </div>

      {/* CHART AND QUICK ACTIONS */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">

        <Panel
          className="xl:col-span-2"
          title="Income & Expenses"
          description={`Monthly ledger activity · ${year}`}
          action={
            <TextLink
              onClick={() =>
                navigate('/reports/profit-loss')
              }
            >
              Profit & Loss
            </TextLink>
          }
        >
          <IncomeExpenseChart
            data={dashboardData.trends}
            currency={currency}
          />
        </Panel>

        <Panel
          title="Quick Actions"
          description="Common accounting tasks"
        >
          <div className="space-y-2">
            <QuickAction
              icon={FiFileText}
              label="Invoices"
              description="View and manage sales invoices"
              onClick={() =>
                navigate('/sales/invoices')
              }
            />

            <QuickAction
              icon={FiBookOpen}
              label="Transactions"
              description="Review accounting entries"
              onClick={() =>
                navigate('/accounting/transactions')
              }
            />

            <QuickAction
              icon={FiLink}
              label="Connect Bank"
              description="Manage banking connections"
              onClick={() =>
                navigate('/accounting/connect-bank')
              }
            />

            <QuickAction
              icon={FiBarChart2}
              label="Financial Reports"
              description="Review accounting statements"
              onClick={() =>
                navigate('/reports/profit-loss')
              }
            />
          </div>
        </Panel>
      </div>

      {/* OPERATIONAL OVERVIEW */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">

        {/* INVOICES */}
        <Panel
          title="Invoices"
          description="Client receivables overview"
          action={
            <TextLink
              onClick={() =>
                navigate('/sales/invoices')
              }
            >
              View Invoices
            </TextLink>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Total invoices
              </p>

              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {dashboardData.invoiceCount}
              </p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Overdue
              </p>

              <p className={`mt-2 text-2xl font-semibold ${
                dashboardData.overdueInvoices > 0
                  ? 'text-rose-600'
                  : 'text-slate-900'
              }`}>
                {dashboardData.overdueInvoices}
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
            <SafeIcon
              icon={FiCalendar}
              className="mt-0.5 shrink-0"
            />

            <p>
              Overdue count is based on invoice due dates
              and payment statuses.
            </p>
          </div>
        </Panel>

        {/* BANKING */}
        <Panel
          title="Banking & Reconciliation"
          description="Connection and ledger review status"
          action={
            <TextLink
              onClick={() =>
                navigate('/accounting/reconciliation')
              }
            >
              Reconcile
            </TextLink>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Connected Banks
              </p>

              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {dashboardData.bankCount}
              </p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Unreconciled Entries
              </p>

              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {dashboardData.unreconciledCount}
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <SafeIcon
              icon={
                dashboardData.lastReconciliationDate
                  ? FiCheckCircle
                  : FiCreditCard
              }
              className="shrink-0 text-slate-400"
            />

            <span>
              Last completed reconciliation:{' '}
              {dashboardData.lastReconciliationDate
                ? formatDate(
                    dashboardData.lastReconciliationDate
                  )
                : 'No completed reconciliation'}
            </span>
          </div>
        </Panel>
      </div>

      {/* RECENT ACCOUNTING ACTIVITY */}
      <Panel
        title="Recent Activity"
        description="Latest entries from the selected client's ledger"
        action={
          <TextLink
            onClick={() =>
              navigate('/accounting/transactions')
            }
          >
            View All
          </TextLink>
        }
      >
        {dashboardData.recentActivity.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
              <SafeIcon
                icon={FiActivity}
                className="text-xl"
              />
            </div>

            <p className="mt-4 text-sm font-semibold text-slate-800">
              No accounting activity yet
            </p>

            <p className="mt-2 max-w-sm text-xs leading-relaxed text-slate-500">
              Journal activity will appear here as
              transactions are recorded for this client.
            </p>

            <button
              type="button"
              onClick={() =>
                navigate('/accounting/transactions')
              }
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Go to Transactions
              <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {dashboardData.recentActivity.map(
              (transaction) => (
                <div
                  key={
                    transaction.batch_id ||
                    transaction.id
                  }
                  className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <SafeIcon
                      icon={FiBookOpen}
                      className="text-sm"
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-slate-800">
                      {transaction.description ||
                        transaction.payee ||
                        'Ledger entry'}
                    </p>

                    <p className="mt-1 text-[11px] text-slate-500">
                      {formatDate(transaction.date)}
                    </p>
                  </div>

                  {transaction.status && (
                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium capitalize text-slate-600">
                      {String(transaction.status)
                        .replace(/_/g, ' ')
                        .toLowerCase()}
                    </span>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </Panel>

      {/* DATA NOTE */}
      <p className="px-1 text-[11px] leading-relaxed text-slate-400">
        Overview metrics summarize the ledger entries
        available to this workspace. Cash movement is not
        an opening or closing bank balance. For formal
        accounting statements, use Financial Reports.
        Data may be cached briefly; select Refresh for
        the latest available records.
      </p>
    </div>
  );
}